/**
 * Tilstanden til RBAC-oppslaget (`useMenuPermissions`), og hva den betyr for
 * tilgang og navigasjon.
 *
 * Egen fil fordi regelen er ren og dermed testbar, og fordi den bodde skjult i
 * `AuthGate` i `App.tsx` der ingen test nådde den. Det var der feilen i
 * VIB-2771 fikk leve: `AuthGate` leste `isLoading` fra React Query, og i v5 er
 * `isLoading === isPending && isFetching`. Det finnes derfor et vindu der
 * `isLoading` er `false` samtidig som dataene mangler — query-en er montert,
 * men henting er ikke startet. I det vinduet falt tilgangssjekken ned på
 * fail-CLOSED, alt ble utilgjengelig, og `/` sendte brukeren til Hjelp med en
 * `replace`-navigasjon som aldri ble revurdert.
 */

export type RbacTilstand = "laster" | "feilet" | "klar";

export interface RbacQuery {
  /** React Query `isPending`, IKKE `isLoading`. Se forklaringen over. */
  isPending: boolean;
  isError: boolean;
  /** Om oppslaget faktisk har data å svare ut fra. */
  harData: boolean;
}

/**
 * Hvor langt RBAC-oppslaget er kommet.
 *
 * «klar» krever BÅDE at hentingen er ferdig og at det finnes data. Mangler
 * dataene etter at hentingen er ferdig, er det en feil, ikke en tom liste:
 * en tom liste ville betydd «ingen har tilgang til noe».
 */
export function rbacTilstand({ isPending, isError, harData }: RbacQuery): RbacTilstand {
  if (isPending) return "laster";
  if (isError || !harData) return "feilet";
  return "klar";
}

/**
 * Fail-OPEN mens vi laster, fail-CLOSED når oppslaget har feilet.
 *
 * Fail-open under lasting er bevisst: uten den blinker menyen tom i det
 * halvsekundet oppslaget tar. Fail-closed ved feil er like bevisst: en feilet
 * RBAC-henting skal ikke gi alle full tilgang.
 */
export function failOpen(tilstand: RbacTilstand): boolean {
  return tilstand === "laster";
}

/**
 * Om vi har lov til å sende brukeren videre basert på tilgangene.
 *
 * Navigasjonen fra `/` er en `replace`, altså ugjenkallelig: lander brukeren
 * på Hjelp fordi tilgangene ikke var kommet, finnes det ingen vei tilbake i
 * den økten. Derfor venter vi heller enn å gjette. Ved «feilet» navigerer vi
 * fortsatt, fordi da kommer dataene aldri, og å bli stående å vente for alltid
 * er verre enn å havne på Hjelp.
 */
export function kanNavigere(tilstand: RbacTilstand): boolean {
  return tilstand !== "laster";
}
